import { Money } from "types/landlord/reports";
import type { Address } from "shared/types";
import {
  AccountStatusVariant,
  PendingIdentityVerificationStatus,
  LandlordVerificationStatus,
  LeaseVerificationStatus,
  RvStatus
} from "views/rent-reporting/constants";

export interface RentReportingData {
  id: number;
  for_month: string;
  rent_amount: Money;
  available_delinquency_options: {
    name: string;
    months_delinquency: number;
  }[];
  status: RvStatus;
  months_delinquency: number;
}

type AuditLog = {
  actor_id: string | number;
  actor_type: string;
  additional_data: unknown;
  auditable_id: number;
  auditable_type: string;
  created_at: string;
  id: number;
  event: string;
  version: number;
  changes: unknown;
};

export interface EnrollmentCase {
  id: number;
  property?: {
    name: string;
  };
  address?: Address;
}

export interface Subscriber {
  account_status: AccountStatusVariant;
  address: string;
  can_resubscribe?: boolean;
  dob: string;
  email: string;
  id: number;
  lease_end_date: string;
  monthly_rent_amount: string;
  move_in_date: string;
  move_out_date: string;
  name?: string;
  phone: string;
  property?: { id?: number; name?: string; address?: string };
  unit: string;
  unit_id?: number;
  subscription_status: SubscriptionStatuses;
  subscription_end: string;
  subscription_start: string;
  verification_link?: string;
  opt_in_link?: string;
  opt_out_link?: string;
  first_name?: string;
  last_name?: string;
  lease_month_to_month?: boolean;
  moved_out?: boolean;
  current_month_payment?: Money;
  audit_logs?: AuditLog[];
  case_status?: string;
  remaining_opt_in_days?: number;
  remaining_opt_out_days?: number;
  ssn?: string;
  active_enrollment?: {
    consent_type: string;
    created_at: string;
    approved_at: string | null;
    unenrollment_date: string | null;
  };
  enrollment_cases?: EnrollmentCase[];
  identity_verification_status?: PendingIdentityVerificationStatus;
  lease_verification_status?: LeaseVerificationStatus;
  landlord_verification_status?: LandlordVerificationStatus;
}

export enum SubscriptionStatuses {
  EXPIRED = "expired",
  ACTIVE = "active",
  INACTIVE = "inactive"
}

type Actor = {
  created_at: string;
  email: string;
  id: number;
  name: string;
  partner_id: number;
  password_digest: string;
  updated_at: string;
};

interface AuditableEntity {
  close_tradeline: boolean;
  created_at: string;
  delete_tradeline: boolean;
  for_month: string;
  has_pending_plaid_transactions: boolean;
  has_pending_pms_transactions: boolean;
  id: number;
  metro2_generated_at: string;
  metro2_s3_url: string;
  monthly_metro2_upload_id: number;
  months_delinquency: number;
  note: string;
  rent_amount_cents: number;
  rent_amount_currency: string;
  reporting_account_id: number;
  status: string;
  transaction_data: unknown;
  transaction_ids: unknown;
  updated_at: string;
}

export interface AudiLog {
  actor: Actor;
  auditable_entity: AuditableEntity;
  auditable_id: number;
  auditable_type: AuditableType;
  changes?: LogChangesType;
  created_at: string;
  event: LogEvent;
  id: number;
  version: number;
  messages?: string[];
}

export type LogChangesType = {
  [key: string]: [string, string] | [boolean, boolean];
};

export enum LogEvent {
  CREATE = "create",
  UPDATE = "update",
  REQUIRE_MANUAL_VERIFICATION = "require_manual_verification",
  MANUAL_VERIFY = "manual_verify",
  RESIDENT_OPTED_OUT = "resident_opted_out",
  AUTO_OPTED_OUT = "auto_opted_out",
  RESIDENT_OPTED_IN = "resident_opted_in",
  AUTO_OPTED_IN = "auto_opted_in",
  APPROVE = "approve",
  ADMIN_SUBMIT_ENROLLMENT = "admin_submit_enrollment",
  SIGN_IN_OPTED_IN = "sign_in_opted_in",
  UNENROLL_ONGOING = "unenroll_ongoing",
  UNENROLL_BY_PARTNER = "unenroll_by_partner",
  UNENROLL_BY_ADMIN = "unenroll_by_admin",
  UNENROLL_BY_45_DAYS_PENDING_POLICY = "unenroll_by_45_days_pending_policy",
  UNENROLL_MOVE_OUT = "unenroll_move_out",
  UNENROLL_EVICTION = "unenroll_eviction",
  UNENROLL_PROPERTY_DEACTIVATED = "unenroll_property_deactivated",
  REVERSE_OPT_OUT = "reverse_opt_out",
  REVERSE_UNENROLL = "reverse_unenroll",
  RECONSIDER = "reconsider"
}

export enum AuditableType {
  BOOM_PARTNERS_CUSTOMER = "Boom::PartnersCustomer",
  BOOM_CUSTOMER = "Boom::Customer",
  BOOM_ENROLLMENT = "Boom::ReportingAccount::Enrollment"
}

export enum UpdateFields {
  PHONE = "phone",
  FIRST_NAME = "first_name",
  LAST_NAME = "last_name",
  DOB = "dob"
}

export const UpdateTitles: { [key in UpdateFields]?: string } = {
  [UpdateFields.PHONE]: "phone number",
  [UpdateFields.FIRST_NAME]: "first name",
  [UpdateFields.LAST_NAME]: "last name",
  [UpdateFields.DOB]: "date of birth"
};

type Transaction = {
  transaction_id: string;
  date: string;
  amount: number;
  iso_currency_code: string;
  description: string;
  type: string;
  category?: string;
};

export interface CustomerTransactionData {
  [key: string]: unknown;
  data: Transaction[];
}
